import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowRight,
    faBullhorn,
    faChartLine,
    faCode,
    faGraduationCap,
    faLightbulb,
    faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import PublicNavBar from '../components/layout/PublicNavBar';
import PublicFooter from '../components/layout/PublicFooter';
import { usePageMeta } from '../hooks/usePageMeta';

const BlogPage: FC = () => {
    usePageMeta({ title: 'Blog', description: 'Insights, tutorials, and best practices for algorithmic trading and quantitative strategy research from the Quanthop team.' });

    return (
        <div className="min-h-screen bg-[#0a0e14] text-gray-100 font-[Poppins] selection:bg-[#9d4edd]/30">
            <PublicNavBar />

            {/* ════════════════ HERO ════════════════ */}
            <section className="relative pt-36 pb-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0f1520] via-[#0a0e14] to-[#080b11]" />
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.15) 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

                <div className="max-w-[680px] mx-auto relative z-10 text-center">
                    <h1 className="text-3xl sm:text-[2.25rem] md:text-[2.5rem] font-bold leading-[1.1] tracking-[-0.025em] mb-5 text-white">
                        Blog
                    </h1>

                    <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3">
                        Insights, tutorials, and best practices for algorithmic trading
                        and quantitative strategy research.
                    </p>

                    <p className="text-[14px] text-white/60 italic mb-10">
                        Coming soon.
                    </p>

                    <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-gray-400">
                        <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                        Actively being written
                    </div>
                </div>
            </section>

            {/* ════════════════ PLANNED TOPICS ════════════════ */}
            <section className="py-16 px-4 sm:px-6 lg:px-10 bg-white">
                <div className="max-w-[960px] mx-auto">
                    <div className="text-center mb-14">
                        <h2 className="text-xl md:text-[24px] font-bold text-gray-950 tracking-[-0.015em] mb-5">
                            Planned topics
                        </h2>
                        <p className="text-[15px] text-gray-600 leading-[1.7] max-w-[560px] mx-auto">
                            Articles will cover the full spectrum of quantitative strategy
                            research, from beginner guides to advanced validation
                            techniques.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[
                            {
                                icon: faGraduationCap,
                                title: 'Getting Started',
                                desc: 'Beginner-friendly guides on algorithmic trading fundamentals and building your first strategy.',
                            },
                            {
                                icon: faCode,
                                title: 'Strategy Development',
                                desc: 'QSL language patterns, indicator recipes, and techniques for writing robust trading strategies.',
                            },
                            {
                                icon: faChartLine,
                                title: 'Optimization',
                                desc: 'Parameter tuning, walk-forward analysis, and methods to avoid overfitting.',
                            },
                            {
                                icon: faShieldHalved,
                                title: 'Risk Management',
                                desc: 'Position sizing, drawdown control, and portfolio-level risk techniques for algorithmic traders.',
                            },
                            {
                                icon: faLightbulb,
                                title: 'Research Insights',
                                desc: 'Case studies, quantitative research notes, and lessons from real strategy development cycles.',
                            },
                            {
                                icon: faBullhorn,
                                title: 'Platform Updates',
                                desc: 'New features, changelog highlights, and roadmap previews for the Quanthop platform.',
                            },
                        ].map((topic) => (
                            <div
                                key={topic.title}
                                className="rounded-lg border border-gray-200/80 bg-white px-6 py-5 shadow-sm"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-9 h-9 rounded-lg bg-[#f3e8ff] flex items-center justify-center flex-shrink-0">
                                        <FontAwesomeIcon icon={topic.icon} className="text-sm text-[#9d4edd]" />
                                    </div>
                                    <h3 className="text-[14px] font-semibold text-gray-950">{topic.title}</h3>
                                </div>
                                <p className="text-[13px] text-gray-500 leading-[1.6]">{topic.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ════════════════ CTA ════════════════ */}
            <section className="relative py-28 px-4 sm:px-6 lg:px-10 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-t from-[#9d4edd]/[0.03] to-transparent pointer-events-none" />
                <div className="max-w-[600px] mx-auto text-center relative z-10">
                    <h2 className="text-xl md:text-[24px] font-bold text-white tracking-[-0.02em] mb-5">
                        Start building while we write
                    </h2>
                    <p className="text-[15px] text-gray-300/70 leading-[1.7] mb-3">
                        The platform is fully functional. Articles are being written
                        alongside active development.
                    </p>
                    <p className="text-[13px] text-gray-400/60 leading-[1.7] mb-10">
                        Explore the research pipeline to see what Quanthop offers.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4">
                        <Link
                            to="/pricing"
                            className="inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
                        >
                            Start Research
                            <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        </Link>
                        <Link
                            to="/features/research-pipeline"
                            className="text-[13px] text-gray-400 hover:text-gray-200 transition-colors"
                        >
                            View the research pipeline <span className="text-gray-600">&rarr;</span>
                        </Link>
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
};

export default BlogPage;
